import { ReactNode, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import Image from '@cartwhl/ui/atoms/Image';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { productStore } from 'stores';

type Slide = 1;

export default function ReadyForMoreSpinsModal({
  close,
}: {
  close: () => void;
}) {
  const slide = 1 as Slide;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };
  const slides = {
    1: {
      title: "You're ready for more Spins!",
      component: (
        <Slide
          id={1}
          activeSlide={slide}
          close={close}
          text={
            <>
              Nice work, you're ready for more Spins. You can go Pro to get even
              more Spins, along with more powerful features. If Pro is not your
              thing, you can always buy Spins a la carte. See all the{' '}
              <a
                href="https://getcartwheel.com/pricing"
                target="_blank"
                className="text-blue underline"
              >
                benefits of Pro here
              </a>
              .
            </>
          }
        />
      ),
    },
  };

  return (
    <Modal
      title={
        <Text as="h3" variant="studio/body/medium/md" className="font-display">
          {slides[slide].title}
        </Text>
      }
      open={true}
      onOpenChange={handleOpenChange}
      closeOnClickOutside={false}
    >
      {slides[slide].component}
    </Modal>
  );
}

function Slide({
  text,
  close,
}: {
  id: Slide;
  text: ReactNode;
  close: () => void;
  activeSlide: number;
}) {
  const closeMe = () => {
    close();
  };

  const { products } = useSnapshot(productStore.state);
  const quantity = 1;

  const spinPriceObject = productStore.getPriceByID(
    products?.more?.default_price ?? '',
  );
  const proPriceObject = productStore.getPriceByID(
    products?.pro?.default_price ?? '',
  );
  const spinPrice = spinPriceObject?.unit_amount ?? 500;
  const proPrice = proPriceObject?.unit_amount ?? 3900;
  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  const handleSpinsCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      spinPriceObject,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  const handleProCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      proPriceObject,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  return (
    <div>
      <Image
        src={
          'https://resources.getcartwheel.com/share/images/modals/more_spins.webp'
        }
        alt={`Cartwheel characters celebrating.`}
        className="bg-gray-15 mb-4 block aspect-452/254 w-full rounded-2xl"
      />
      <Text as="p" variant="studio/body/sm" className="font-normal">
        {text}
      </Text>
      <div className="mt-6 flex gap-4">
        <button
          onClick={handleSpinsCheckout}
          className="bg-gray-15 hover:bg-gray-30 dark:bg-gray-75 dark:hover:bg-gray-60 relative flex flex-1 flex-col items-start justify-between rounded-xl p-6 transition-colors"
        >
          <Text as="span" variant="studio/title/md">
            Buy
          </Text>
          <div className="flex items-baseline">
            <Text
              as="span"
              variant="studio/display/xl"
              className="text-5xl font-bold leading-none"
            >
              150
            </Text>
            <Text as="span" variant="studio/title/md" className="ml-1 text-lg">
              Spins
            </Text>
          </div>
          <div className="mt-4 flex items-baseline">
            <Text as="span" variant="studio/body/sm">
              Total:
            </Text>
            <Text as="span" variant="studio/body/sm" className="ml-1 font-medium">
              ${productStore.formatMoney(quantity * spinPrice)}.00
            </Text>
          </div>
          <span className="absolute right-4 top-1/2 -translate-y-1/2 transform text-xl">
            <ChevronRightIcon />
          </span>
        </button>
        <button
          onClick={handleProCheckout}
          className="bg-blue/10 border-blue hover:bg-blue/20 dark:hover:bg-blue/30 relative flex flex-1 flex-col items-start justify-between rounded-xl border p-6 transition-colors"
        >
          <Text as="span" variant="studio/title/md">
            Upgrade to
          </Text>
          <Text as="span" variant="studio/display/xl" className="leading-none">
            Pro
          </Text>
          <div className="mt-4 flex items-baseline">
            <Text as="span" variant="studio/body/sm">
              Total:
            </Text>
            <Text as="span" variant="studio/body/sm" className="ml-1 font-medium">
              ${productStore.formatMoney(proPrice)}.00
            </Text>
          </div>
          <span className="absolute right-4 top-1/2 -translate-y-1/2 transform text-xl">
            <ChevronRightIcon />
          </span>
        </button>
      </div>
      <div className="mt-6 flex justify-start">
        <Button onClick={closeMe}>Close</Button>
      </div>
    </div>
  );
}
